const LinkedProductNavigation = {
  isLoading: false,
  initialized: false,
  MAX_SECTIONS_PER_REQUEST: 5,

  THEME_SCRIPT_PATTERNS: [
    "product-info.js",
    "product-form.js",
    "main-product.js",
    "linked-product-selector.js",
    "magnify.js",
    "show-more.js",
    "price-per-item.js",
    "theme-editor.js",
    "block-buy-x-get-y.js",
    "media-gallery.js",
    "product-modal.js",
    "product-model.js",
    "pickup-availability.js",
    "quick-add.js",
    "pdp-free-gift.js",
  ],

  THIRD_PARTY_SCRIPT_PATTERNS: [
    "shopify-afterpay-javascript.js",
    "static.afterpay.com",
    "restockrocket-product.js",
    "restockrocket-product-bis.js",
  ],

  // App scripts that must re-bind after DOM replace (same src is cached otherwise).
  FORCE_RELOAD_SCRIPT_PATTERNS: ["rates.js"],

  mobileAddToCartObserver: null,

  init() {
    if (this.initialized) return;
    this.initialized = true;
  },

  getUrlFromLocation() {
    return `${window.location.pathname}${window.location.search}`;
  },

  getProductSectionIds() {
    const mainContent = document.getElementById("MainContent");
    if (!mainContent) return [];

    const sectionIds = Array.from(
      mainContent.querySelectorAll('[id^="shopify-section-"]')
    ).map((section) => section.id.replace("shopify-section-", ""));

    return this.orderSectionIds(sectionIds);
  },

  orderSectionIds(sectionIds) {
    const orderedIds = [...sectionIds];
    const mainIndex = orderedIds.findIndex((id) => id.endsWith("__main"));

    if (mainIndex > 0) {
      const [mainSectionId] = orderedIds.splice(mainIndex, 1);
      orderedIds.unshift(mainSectionId);
    }

    return orderedIds;
  },

  isMainProductSection(sectionId) {
    return sectionId.endsWith("__main");
  },

  chunkArray(array, size) {
    const chunks = [];

    for (let index = 0; index < array.length; index += size) {
      chunks.push(array.slice(index, index + size));
    }

    return chunks;
  },

  updateCurrentProductUrl(url) {
    document.querySelectorAll("linked-product-selects").forEach((element) => {
      element.dataset.currentProductUrl = url;
    });
  },

  updateBrowserHistory(url) {
    window.history.replaceState({ linkedProductSelection: true, url }, "", url);

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = new URL(url, window.location.origin).href;
    }
  },

  updateDocumentTitle(mainSection) {
    const productTitle = mainSection
      ?.querySelector(".product__title, .product__info-container h1, h1")
      ?.textContent?.trim();

    if (!productTitle || !document.title) return;

    const onlineSuffixMatch = document.title.match(/\s+online\s*\|.*$/i);
    const siteSuffixMatch = document.title.match(/\s*\|.*$/);
    const titleSuffix = onlineSuffixMatch?.[0] || siteSuffixMatch?.[0] || "";
    document.title = productTitle + titleSuffix;
  },

  updateLoadingState(isLoading, message = "") {
    const selector = document.querySelector("linked-product-selects");
    if (!selector) return;

    selector.classList.toggle("is-loading", isLoading);
    selector.setAttribute("aria-busy", String(isLoading));

    const status = selector.querySelector("[data-linked-product-status]");
    if (status) status.textContent = message;
  },

  shouldSkipExternalScript(script) {
    if (!script.src) return false;

    if (
      [...this.THEME_SCRIPT_PATTERNS, ...this.THIRD_PARTY_SCRIPT_PATTERNS].some(
        (pattern) => script.src.includes(pattern)
      )
    ) {
      return true;
    }

    // FusedShip ships its own jQuery — don't redeclare it on every swap.
    if (/jquery[\w.-]*\.min\.js|jquery[\w.-]*\.js/i.test(script.src) && window.jQuery) {
      return true;
    }

    return false;
  },

  shouldForceReloadExternalScript(script) {
    if (!script?.src) return false;

    return this.FORCE_RELOAD_SCRIPT_PATTERNS.some((pattern) =>
      script.src.includes(pattern)
    );
  },

  shouldSkipInlineScript(script) {
    const code = script.textContent || "";

    if (
      code.includes("[data-afterpay-pdp-messaging]") &&
      code.includes("MutationObserver")
    ) {
      return true;
    }

    return false;
  },

  isExecutableScript(script) {
    const type = (script.getAttribute("type") || "").trim().toLowerCase();

    return (
      type === "" ||
      type === "module" ||
      type === "text/javascript" ||
      type === "application/javascript" ||
      type === "text/ecmascript" ||
      type === "application/ecmascript"
    );
  },

  executeInlineScript(code) {
    let transformed = code;

    // Page already loaded — unwrap deferred bootstraps so app inits still run.
    if (document.readyState !== "loading") {
      transformed = transformed
        .replace(
          /document\.addEventListener\(\s*['"]DOMContentLoaded['"]\s*,\s*function\s*\(\)\s*\{([\s\S]*?)\}\s*\)\s*;/g,
          "$1"
        )
        .replace(
          /window\.addEventListener\(\s*['"]load['"]\s*,\s*function\s*\(\)\s*\{([\s\S]*?)\}\s*\)\s*;/g,
          "$1"
        );
    }

    new Function(transformed)();
  },

  runScript(oldScript) {
    if (oldScript.src) {
      return this.reloadExternalScript(oldScript);
    }

    if (!oldScript.textContent) {
      oldScript.remove();
      return Promise.resolve();
    }

    try {
      this.executeInlineScript(oldScript.textContent);
    } catch (error) {
      console.error("Section script reinit failed", error);
    }

    oldScript.remove();
    return Promise.resolve();
  },

  reloadExternalScript(oldScript) {
    return new Promise((resolve) => {
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attribute) => {
        if (attribute.name === "src") return;
        newScript.setAttribute(attribute.name, attribute.value);
      });
      newScript.removeAttribute("defer");
      newScript.removeAttribute("async");

      if (this.shouldForceReloadExternalScript(oldScript)) {
        const url = new URL(oldScript.src, window.location.origin);
        url.searchParams.set("linked_reload", String(Date.now()));
        newScript.src = url.toString();
      } else {
        newScript.src = oldScript.src;
      }

      newScript.onload = () => resolve();
      newScript.onerror = () => resolve();
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  },

  async reinitializeSection(section) {
    const scripts = Array.from(section.querySelectorAll("script"));
    const externalScripts = [];
    const inlineScripts = [];

    scripts.forEach((oldScript) => {
      // JSON, JSON-LD and app data scripts are content, not executable code.
      // Keep them in the replaced section for SEO and for components that read
      // configuration from the DOM after their connectedCallback runs.
      if (!this.isExecutableScript(oldScript)) return;

      if (
        this.shouldSkipExternalScript(oldScript) ||
        this.shouldSkipInlineScript(oldScript)
      ) {
        oldScript.remove();
        return;
      }

      if (oldScript.src) {
        externalScripts.push(oldScript);
      } else {
        inlineScripts.push(oldScript);
      }
    });

    // Load external app scripts first (e.g. Machship rates.js), then run inline
    // initializers that call into those APIs (ShippingCalculator.show).
    for (const script of externalScripts) {
      await this.runScript(script);
    }

    for (const script of inlineScripts) {
      await this.runScript(script);
    }

    document.dispatchEvent(
      new CustomEvent("shopify:section:load", {
        detail: { sectionId: section.id },
        bubbles: true,
      })
    );

    this.reinitializeSectionWidgets(section);

    if (this.isMainProductSection(section.id.replace("shopify-section-", ""))) {
      this.refreshAfterpayMessaging(section);
    }
  },

  refreshAfterpayMessaging(section) {
    const block = section.querySelector("[data-afterpay-pdp-messaging]");
    if (!block) return;

    const priceMarker = block.querySelector(".afterpay-product-price");
    if (priceMarker) {
      const price = priceMarker.textContent;
      priceMarker.textContent = "";
      priceMarker.textContent = price;
    }

    block
      .querySelectorAll('square-placement[data-page-type="product"]')
      .forEach((placement) => {
        const productScope =
          placement.closest('[id^="ProductInfo-"], .product__info-container') ||
          section;
        const addToCartButton = productScope.querySelector(
          'button[name="add"], .product-form__submit'
        );
        const isAvailable =
          Boolean(addToCartButton) &&
          !addToCartButton.disabled &&
          addToCartButton.getAttribute("aria-disabled") !== "true";

        placement.toggleAttribute("data-ozt-unavailable", !isAvailable);
      });
  },

  reinitializeSectionWidgets(section) {
    section.querySelectorAll("splide-slider-component").forEach((element) => {
      if (typeof element.ensureMarkup === "function") {
        element.ensureMarkup();
      }

      if (typeof element.initSlider === "function") {
        element.initSlider();
      }
    });

    if (
      section.querySelector("product-recommendations, #third_slide") &&
      section.id
    ) {
      this.reinitializeRelatedProducts(section);
    }
  },

  reinitializeRelatedProducts(section) {
    const mobileSlider = section.querySelector("#third_slide");

    if (
      !mobileSlider?.dataset.url ||
      !window.matchMedia("(max-width: 750px)").matches ||
      typeof Splide === "undefined"
    ) {
      return;
    }

    fetch(mobileSlider.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (!recommendations?.innerHTML.trim()) return;

        mobileSlider.innerHTML = recommendations.innerHTML;

        new Splide("#third_slide", {
          type: "loop",
          perPage: 2,
          autoplay: false,
          interval: 5000,
          pauseOnHover: true,
          gap: "1.8rem",
          arrows: false,
        }).mount();
      })
      .catch((error) => {
        console.error("Related products carousel reinit failed", error);
      });
  },

  parseSectionElement(sectionHtml, sectionId) {
    if (!sectionHtml) return null;

    const template = document.createElement("template");
    template.innerHTML = sectionHtml.trim();

    return (
      template.content.querySelector(`#shopify-section-${sectionId}`) ||
      template.content.firstElementChild
    );
  },

  // Gallery media uses scroll-trigger fade-in. On AJAX replace that restarts from
  // opacity 0.01 while the buy box is already visible — reads as a zoom jump.
  cancelScrollRevealAnimations(root) {
    if (!root) return;

    root.querySelectorAll(".scroll-trigger").forEach((element) => {
      element.classList.add("scroll-trigger--cancel");
      element.classList.remove("scroll-trigger--offscreen");
    });
  },

  warmSectionMedia(section) {
    const images = Array.from(
      section.querySelectorAll(
        ".product__media-wrapper img, .product__media-item.is-active img"
      )
    ).slice(0, 4);

    images.forEach((image) => {
      if (image.loading === "lazy") image.loading = "eager";
      if (image.getAttribute("fetchpriority") == null) {
        image.setAttribute("fetchpriority", "high");
      }
    });

    return Promise.all(
      images.map((image) => {
        if (typeof image.decode === "function") {
          return image.decode().catch(() => {});
        }

        if (image.complete) return Promise.resolve();

        return new Promise((resolve) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        });
      })
    );
  },

  removeStaleSectionModals(newSection) {
    newSection
      .querySelectorAll("product-modal[id], modal-dialog[id]")
      .forEach((newModal) => {
        document.getElementById(newModal.id)?.remove();
      });
  },

  async replaceSection(sectionId, sectionHtml) {
    if (!sectionHtml) return null;

    const currentSection = document.getElementById(
      `shopify-section-${sectionId}`
    );

    if (!currentSection) return null;

    const newSection = this.parseSectionElement(sectionHtml, sectionId);
    if (!newSection) return null;

    // Must run before first paint of the new nodes.
    this.cancelScrollRevealAnimations(newSection);

    const mediaWrapper = currentSection.querySelector(".product__media-wrapper");
    const nextMediaWrapper = newSection.querySelector(".product__media-wrapper");

    if (mediaWrapper && nextMediaWrapper) {
      nextMediaWrapper.style.minWidth = `${mediaWrapper.offsetWidth}px`;
      nextMediaWrapper.style.minHeight = `${mediaWrapper.offsetHeight}px`;
    }

    // ModalDialog moves itself to document.body when connected, so it sits
    // outside the section being replaced. Remove the old copy before the new
    // section connects or its opener will resolve the stale duplicate ID.
    this.removeStaleSectionModals(newSection);
    currentSection.replaceWith(newSection);

    if (this.isMainProductSection(sectionId)) {
      await this.warmSectionMedia(newSection);
    }

    if (nextMediaWrapper) {
      nextMediaWrapper.style.minWidth = "";
      nextMediaWrapper.style.minHeight = "";
    }

    await this.reinitializeSection(newSection);
    return newSection;
  },

  reinitializeProductForms(root) {
    root.querySelectorAll("product-form").forEach((productForm) => {
      if (typeof productForm._tryQueryElements === "function") {
        productForm._tryQueryElements();
        productForm._ensureListeners();
      }
    });
  },

  initializeMobileAddToCart(mainSection) {
    if (window.innerWidth > 1025) return;

    if (this.mobileAddToCartObserver) {
      this.mobileAddToCartObserver.disconnect();
      this.mobileAddToCartObserver = null;
    }

    const submitButton = mainSection.querySelector(
      ".product-form__submit--mobile"
    );

    if (!submitButton) return;

    submitButton.classList.remove("product-form__submit--fixed");

    const submitButtonContainer =
      submitButton.closest(".product__info-container") ||
      submitButton.parentElement;

    if (!submitButtonContainer) return;

    const calculateRootMargin = () => {
      const viewportHeight = window.innerHeight;
      const containerRect = submitButtonContainer.getBoundingClientRect();
      const marginTop = Math.max(
        containerRect.top - viewportHeight,
        -viewportHeight
      );

      return `${marginTop}px 0px 0px 0px`;
    };

    this.mobileAddToCartObserver = new IntersectionObserver(
      ([entry]) => {
        submitButton.classList.toggle(
          "product-form__submit--fixed",
          !entry.isIntersecting
        );
      },
      {
        threshold: 0,
        rootMargin: calculateRootMargin(),
      }
    );

    this.mobileAddToCartObserver.observe(submitButtonContainer);
  },

  initializeStickyBar() {
    document.querySelector("pdp-sticky-bar")?.initialize();
  },

  reinitializeMainProductSection(mainSection) {
    if (!mainSection) return;

    this.reinitializeProductForms(mainSection);
    this.initializeMobileAddToCart(mainSection);
    this.initializeStickyBar(mainSection);
    this.refreshAfterpayMessaging(mainSection);
    this.syncMachshipShippingVisibility(mainSection);

    if (window.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }
  },

  getPrimaryAddToCartButton(root = document) {
    return (
      root.querySelector(
        '.product__info-container .product-form__submit:not(.product-form__submit--mobile)'
      ) || root.querySelector('.product__info-container button[name="add"]')
    );
  },

  refreshThirdPartyWidgets() {
    this.refreshYotpoWidgets();

    if (window.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }

    window.dispatchEvent(new Event("resize"));
  },

  refreshYotpoWidgets() {
    const init =
      window.yotpoWidgetsContainer?.initWidgets ||
      window.yotpo?.initWidgets ||
      window.yotpo?.refreshWidgets;

    if (typeof init !== "function") return;

    // Reviews section markup is brand new — give the browser a paint before Yotpo mounts.
    requestAnimationFrame(() => {
      try {
        init.call(window.yotpoWidgetsContainer || window.yotpo);
      } catch (error) {
        console.error("Yotpo refresh failed:", error);
      }
    });
  },

  syncMachshipShippingVisibility(mainSection) {
    const calculator =
      mainSection?.querySelector(".machship-shipping-calculator") ||
      document.querySelector(".machship-shipping-calculator");

    if (!calculator) return false;

    const addToCartButton = this.getPrimaryAddToCartButton(
      mainSection || document
    );
    const isUnavailable =
      !addToCartButton ||
      addToCartButton.disabled ||
      addToCartButton.getAttribute("aria-disabled") === "true" ||
      /sold out/i.test(addToCartButton.textContent || "");

    calculator.style.display = isUnavailable ? "none" : "";
    return !isUnavailable;
  },

  async refreshMachshipShipping(mainSection) {
    const isVisible = this.syncMachshipShippingVisibility(mainSection);
    if (!isVisible) return;

    const root = mainSection || document;
    const zip = root.querySelector("#address_zip");
    const hasJqueryBindings =
      window.jQuery &&
      zip &&
      typeof window.jQuery._data === "function" &&
      Boolean(window.jQuery._data(zip, "events"));

    if (hasJqueryBindings) return;

    const ratesScript =
      root.querySelector('script[src*="rates.js"]') ||
      document.querySelector('script[src*="rates.js"]');

    if (ratesScript?.src) {
      await new Promise((resolve) => {
        const reload = document.createElement("script");
        const url = new URL(ratesScript.src, window.location.origin);
        url.searchParams.set("linked_reload", String(Date.now()));
        reload.src = url.toString();
        reload.onload = () => resolve();
        reload.onerror = () => resolve();
        ratesScript.replaceWith(reload);
      });
    }

    // rates.js assigns ShippingCalculator inside jQuery.ready (async in jQuery 3).
    await new Promise((resolve) => {
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (window.Shopify?.Cart?.ShippingCalculator?.show || attempts > 20) {
          window.clearInterval(timer);
          resolve();
        }
      }, 50);
    });

    const pre = document.getElementById("preshippingcalc");
    if (pre) pre.style.display = "";
    const post = document.getElementById("postshippingcalc");
    if (post) post.style.display = "none";

    if (window.Shopify?.Cart?.ShippingCalculator?.show) {
      window.Shopify.Cart.ShippingCalculator.show({
        moneyFormat: "${{amount}} AUD",
      });
    }
  },

  refreshBuyXGetY() {
    if (typeof PdpOfferAtcLabels !== "undefined" && PdpOfferAtcLabels.update) {
      PdpOfferAtcLabels.update();
    }
  },

  async refreshRestockRocket(productPath) {
    const restockRocket = window._RestockRocket;
    const config = window._RestockRocketConfig;

    if (!restockRocket || !config?.cachedSettings?.enable_signup_widget) {
      return;
    }

    try {
      const response = await fetch(`${productPath}.js`);

      if (!response.ok) {
        throw new Error(`Product JSON failed (${response.status})`);
      }

      const product = await response.json();
      const variant =
        product.variants?.find(
          (entry) => entry.id === product.selected_or_first_available_variant?.id
        ) || product.variants?.[0];

      if (!variant) return;

      config.product = product;
      config.selected_variant_id = variant.id;
      config.variantsInventoryPolicy = {
        [variant.id]: variant.inventory_policy || "deny",
      };
      config.variantsInventoryQuantity = {
        [variant.id]: variant.inventory_quantity ?? 0,
      };

      document
        .querySelectorAll(
          ".product__info-container .restock-rocket-button-container, .pdp-sticky-bar .restock-rocket-button-container"
        )
        .forEach((element) => element.remove());

      const productForms = document.querySelectorAll(
        '.product__info-container form[action*="/cart/add"], .pdp-sticky-bar form[action*="/cart/add"]'
      );

      if (typeof restockRocket.teardownAddToCartForm === "function") {
        productForms.forEach((form) => restockRocket.teardownAddToCartForm(form));
      }

      if (typeof restockRocket.updateProductContext === "function") {
        restockRocket.updateProductContext(product);
      }

      if (typeof restockRocket.setupAddToCartForm === "function") {
        productForms.forEach((form) => restockRocket.setupAddToCartForm(form));
      }

      if (typeof restockRocket.renderButtonForVariant === "function") {
        restockRocket.renderButtonForVariant(variant.id);
      }

      if (typeof restockRocket.updateAddToCartButton === "function") {
        restockRocket.updateAddToCartButton();
      }

      // RestockRocket sometimes paints after the next frame once forms are live.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (
        !document.querySelector(
          ".product__info-container .restock-rocket-button-product"
        ) &&
        typeof restockRocket.renderButtonForVariant === "function"
      ) {
        restockRocket.renderButtonForVariant(variant.id);
      }
    } catch (error) {
      console.error("RestockRocket refresh failed:", error);
    }
  },

  async fetchSectionBatch(productPath, sectionIds) {
    const params = new URLSearchParams();
    sectionIds.forEach((sectionId) => {
      params.append("sections[]", sectionId);
    });

    const fetchUrl = `${productPath}?${params.toString()}`;
    const response = await fetch(fetchUrl);

    if (!response.ok) {
      throw new Error(
        `Section Rendering API failed (${response.status}) for ${sectionIds.join(", ")}`
      );
    }

    return response.json();
  },

  async fetchAllProductSections(productPath, sectionIds) {
    const batches = this.chunkArray(
      sectionIds,
      this.MAX_SECTIONS_PER_REQUEST
    );
    const sectionResponses = await Promise.all(
      batches.map((batch) => this.fetchSectionBatch(productPath, batch))
    );

    return sectionResponses.reduce((merged, batchResponse) => {
      return Object.assign(merged, batchResponse);
    }, {});
  },

  getScrollAnchorElement() {
    return (
      document.querySelector("linked-product-selects") ||
      document.querySelector(".product__info-container") ||
      document.querySelector(".product__title")
    );
  },

  isDebugEnabled() {
    if (typeof window === "undefined") return false;
    if (window.linkedProductDebug === true) return true;

    try {
      if (window.localStorage?.getItem("linkedProductDebug") === "1") {
        return true;
      }
    } catch (error) {
      // private mode / blocked storage
    }

    return new URLSearchParams(window.location.search).get("linkedDebug") === "1";
  },

  createDebugSession(label) {
    if (!this.isDebugEnabled()) return null;

    const session = {
      label,
      startedAt: performance.now(),
      snapshots: [],
      shifts: [],
      observer: null,
    };

    const takeSnapshot = (phase) => {
      const anchor = this.getScrollAnchorElement();
      const header = document.querySelector(".section-header");
      const title =
        document.querySelector(".product__title, .product__info-container h1, h1");
      const mainSection =
        document.querySelector('[id^="shopify-section-"][id$="__main"]') ||
        document.querySelector("#MainContent > .shopify-section");
      const titleStyles = title ? getComputedStyle(title) : null;
      const htmlStyles = getComputedStyle(document.documentElement);

      const snapshot = {
        phase,
        t: Math.round(performance.now() - session.startedAt),
        scrollY: Math.round(window.scrollY),
        anchorTop: anchor ? Math.round(anchor.getBoundingClientRect().top) : null,
        headerTop: header ? Math.round(header.getBoundingClientRect().top) : null,
        headerH: header ? Math.round(header.offsetHeight) : null,
        mainH: mainSection ? Math.round(mainSection.offsetHeight) : null,
        titleFs: titleStyles ? titleStyles.fontSize : null,
        titleFf: titleStyles ? titleStyles.fontFamily.split(",")[0].replace(/["']/g, "") : null,
        htmlFs: htmlStyles.fontSize,
        tsH: htmlStyles.getPropertyValue("--ts-h").trim() || null,
      };

      session.snapshots.push(snapshot);
      console.log(`[linked-debug] ${phase}`, snapshot);
      return snapshot;
    };

    session.snap = takeSnapshot;

    if (typeof PerformanceObserver !== "undefined") {
      try {
        session.observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (!entry.hadRecentInput && entry.value >= 0.001) {
              const sources = (entry.sources || []).map((source) => {
                const node = source.node;
                return {
                  tag: node?.tagName || null,
                  id: node?.id || null,
                  className:
                    typeof node?.className === "string" ? node.className : null,
                  prev: source.previousRect
                    ? {
                        y: Math.round(source.previousRect.y),
                        h: Math.round(source.previousRect.height),
                      }
                    : null,
                  cur: source.currentRect
                    ? {
                        y: Math.round(source.currentRect.y),
                        h: Math.round(source.currentRect.height),
                      }
                    : null,
                };
              });

              const shift = {
                t: Math.round(performance.now() - session.startedAt),
                value: Number(entry.value.toFixed(4)),
                sources,
              };
              session.shifts.push(shift);
              console.warn("[linked-debug] layout-shift", shift);
            }
          });
        });
        session.observer.observe({ type: "layout-shift", buffered: false });
      } catch (error) {
        console.warn("[linked-debug] PerformanceObserver unavailable", error);
      }
    }

    session.finish = () => {
      session.observer?.disconnect();
      console.groupCollapsed(
        `[linked-debug] summary (${label}) — ${session.shifts.length} shifts`
      );
      console.table(session.snapshots);
      if (session.shifts.length) {
        console.table(
          session.shifts.map((shift) => ({
            t: shift.t,
            value: shift.value,
            nodes: shift.sources
              .map((source) => source.id || source.className || source.tag)
              .filter(Boolean)
              .join(", "),
          }))
        );
      } else {
        console.log("No layout-shift entries captured.");
      }
      console.groupEnd();
    };

    console.info(
      "[linked-debug] on — disable with localStorage.removeItem('linkedProductDebug') or drop ?linkedDebug=1"
    );
    takeSnapshot("start");
    return session;
  },

  setScrollTop(top) {
    const target = Math.max(top, 0);
    document.documentElement.scrollTop = target;
    document.body.scrollTop = target;
    window.scrollTo(0, target);
  },

  // Keep scroll stable during section swaps.
  // Do NOT toggle body overflow/position:fixed — Safari drops sticky header + top-streamer.
  // Do NOT touch sticky-header classes — this theme uses sticky_header_type "always".
  lockViewport() {
    const anchor = this.getScrollAnchorElement();

    document.documentElement.classList.add("linked-product-nav-active");

    return {
      scrollY: window.scrollY,
      anchorTop: anchor ? anchor.getBoundingClientRect().top : null,
    };
  },

  unlockViewport(viewportLock) {
    if (!viewportLock) return;

    const anchor = this.getScrollAnchorElement();
    let targetScrollY = viewportLock.scrollY;

    if (viewportLock.anchorTop != null && anchor) {
      const delta = anchor.getBoundingClientRect().top - viewportLock.anchorTop;
      // Ignore tiny layout noise so we don't nudge scroll enough to flash top-streamer.
      if (Math.abs(delta) >= 2) {
        targetScrollY = Math.max(viewportLock.scrollY + delta, 0);
      }
    }

    if (Math.abs((window.scrollY || 0) - targetScrollY) >= 2) {
      this.setScrollTop(targetScrollY);
    }

    document.documentElement.classList.remove("linked-product-nav-active");

    // Keep always-sticky header in sync without hide/reveal transitions.
    const stickyHeader = document.querySelector("sticky-header");
    if (stickyHeader) {
      stickyHeader.currentScrollTop = window.scrollY || 0;
      stickyHeader.header?.classList.add("shopify-section-header-sticky");
      stickyHeader.header?.classList.remove(
        "shopify-section-header-hidden",
        "animate"
      );
    }

    // Recalc top-streamer offset for sticky header (separate section above header group).
    const topStreamer = document.querySelector('[id^="ts-"]');
    if (topStreamer && !topStreamer.classList.contains("ts--dismissed")) {
      document.documentElement.style.setProperty(
        "--ts-h",
        `${topStreamer.offsetHeight}px`
      );
    }
  },

  beginSwapTransition() {
    const mainContent = document.getElementById("MainContent");
    const mainSection =
      mainContent?.querySelector('[id^="shopify-section-"][id$="__main"]') ||
      mainContent?.querySelector('[id^="shopify-section-"]');

    if (!mainContent || !mainSection) return null;

    const lock = {
      mainContent,
      mainSection,
      minHeight: `${mainSection.offsetHeight}px`,
    };

    mainSection.style.minHeight = lock.minHeight;
    mainContent.classList.add("linked-product-is-swapping");

    return lock;
  },

  async endSwapTransition(lock) {
    const mainContent =
      lock?.mainContent || document.getElementById("MainContent");
    const mainSection =
      lock?.mainSection ||
      mainContent?.querySelector('[id^="shopify-section-"][id$="__main"]') ||
      mainContent?.querySelector('[id^="shopify-section-"]');

    mainContent?.classList.remove("linked-product-is-swapping");

    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (mainSection) {
      mainSection.style.minHeight = "";
    }
  },

  markSelectedLinkedOption(productUrl) {
    document.querySelectorAll("[data-linked-product-url]").forEach((button) => {
      const isSelected = button.dataset.linkedProductUrl === productUrl;
      button.classList.toggle("linked-product-selector__option--selected", isSelected);
      button.setAttribute("aria-pressed", String(isSelected));
    });
  },

  restoreSelectorFocus(productUrl) {
    const selectedButton = Array.from(
      document.querySelectorAll("[data-linked-product-url]")
    ).find((button) => button.dataset.linkedProductUrl === productUrl);

    selectedButton?.focus({ preventScroll: true });
  },

  validateMainProductSections(mainSectionIds, sections) {
    if (!sections || typeof sections !== "object") {
      throw new Error("Section Rendering API returned an invalid response");
    }

    for (const sectionId of mainSectionIds) {
      if (!this.parseSectionElement(sections[sectionId], sectionId)) {
        throw new Error(
          `Section Rendering API returned no main product section (${sectionId})`
        );
      }
    }
  },

  partitionSectionIds(sectionIds) {
    const mainSectionIds = [];
    const belowFoldSectionIds = [];

    sectionIds.forEach((sectionId) => {
      if (this.isMainProductSection(sectionId)) {
        mainSectionIds.push(sectionId);
      } else {
        belowFoldSectionIds.push(sectionId);
      }
    });

    return { mainSectionIds, belowFoldSectionIds };
  },

  async loadProduct(url) {
    if (this.isLoading) return;

    const normalizedUrl = url.split("#")[0];
    const productPath = new URL(normalizedUrl, window.location.origin).pathname;
    const selector = document.querySelector("linked-product-selects");

    if (selector?.dataset.currentProductUrl === normalizedUrl) {
      return;
    }

    const sectionIds = this.getProductSectionIds();
    if (!sectionIds.length) {
      console.error("Linked products: no sections found in #MainContent");
      return;
    }

    this.isLoading = true;
    const shouldRestoreFocus = selector?.contains(document.activeElement);
    this.updateLoadingState(true, "Loading product selection");

    const debug = this.createDebugSession(productPath);

    // Lock before fetch so height changes / scroll anchoring can't move the sticky header.
    const viewportLock = this.lockViewport();
    debug?.snap("locked");
    const swapLock = this.beginSwapTransition();
    const { mainSectionIds, belowFoldSectionIds } =
      this.partitionSectionIds(sectionIds);

    try {
      const sections = await this.fetchAllProductSections(
        productPath,
        sectionIds
      );
      this.validateMainProductSections(mainSectionIds, sections);
      debug?.snap("fetched");

      let replacedCount = 0;
      let mainSection = null;

      // Replace main first so buy box / notify CTA update without waiting on below-fold.
      // (No View Transitions — they flash the sticky header on Safari.)
      for (const sectionId of mainSectionIds) {
        const newSection = await this.replaceSection(
          sectionId,
          sections[sectionId]
        );
        if (!newSection) continue;
        replacedCount += 1;
        mainSection = newSection;
      }
      debug?.snap("main-replaced");

      if (!mainSection) {
        throw new Error(
          "Section Rendering API returned no main product section"
        );
      }

      if (swapLock) {
        swapLock.mainSection = mainSection;
        mainSection.style.minHeight = swapLock.minHeight;
      }

      this.updateCurrentProductUrl(normalizedUrl);
      this.updateBrowserHistory(normalizedUrl);
      this.updateDocumentTitle(mainSection);
      this.markSelectedLinkedOption(normalizedUrl);
      if (shouldRestoreFocus) this.restoreSelectorFocus(normalizedUrl);
      this.reinitializeMainProductSection(mainSection);
      debug?.snap("main-reinited");
      await this.refreshRestockRocket(productPath);
      await this.refreshMachshipShipping(mainSection);
      this.refreshBuyXGetY();
      debug?.snap("main-apps");

      document.dispatchEvent(
        new CustomEvent("linked-product:loaded", {
          detail: { url: normalizedUrl },
        })
      );

      // Below-fold sections after buy CTA is ready.
      for (const sectionId of belowFoldSectionIds) {
        const newSection = await this.replaceSection(
          sectionId,
          sections[sectionId]
        );
        if (newSection) replacedCount += 1;
      }
      debug?.snap("below-fold");

      await this.refreshRestockRocket(productPath);
      await this.refreshMachshipShipping(mainSection);
      this.refreshBuyXGetY();
      this.refreshThirdPartyWidgets();
      debug?.snap("widgets");

      if (replacedCount === 0) {
        throw new Error("Section Rendering API returned no usable sections");
      }
    } catch (error) {
      console.error("Linked products section rendering failed:", error);
      // A linked-product choice is still an ordinary product navigation. If
      // progressive enhancement fails, complete it with a normal page load so
      // the UI, URL and cart form can never remain on different products.
      window.location.assign(normalizedUrl);
    } finally {
      await this.endSwapTransition(swapLock);
      debug?.snap("swap-ended");
      this.unlockViewport(viewportLock);
      debug?.snap("unlocked");
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      debug?.snap("after-paint");
      debug?.finish();
      this.isLoading = false;
      const loadedTitle = document
        .querySelector(".product__title, .product__info-container h1, h1")
        ?.textContent?.trim();
      this.updateLoadingState(
        false,
        loadedTitle ? `${loadedTitle} selected` : "Product selection loaded"
      );

    }
  },
};

LinkedProductNavigation.init();
window.LinkedProductNavigation = LinkedProductNavigation;

if (!customElements.get("linked-product-selects")) {
  customElements.define(
    "linked-product-selects",
    class LinkedProductSelects extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener("click", this.onClick);

        if (!this.dataset.entryUrl) {
          this.dataset.entryUrl = LinkedProductNavigation.getUrlFromLocation();
        }

        if (!this.dataset.currentProductUrl) {
          this.dataset.currentProductUrl = this.dataset.entryUrl;
        }
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onClick);
      }

      onClick(event) {
        const button = event.target.closest("[data-linked-product-url]");
        if (!button || button.disabled) return;

        event.preventDefault();

        const url = button.dataset.linkedProductUrl;
        if (!url || url === this.dataset.currentProductUrl) return;

        LinkedProductNavigation.loadProduct(url);
      }
    }
  );
}
